<template>
  <!--
    TV-2188: offered when CreateCheckoutPaymentIntent returned a migration_offer
    — the customer's legacy balance would pay for this order in full once it is
    on Chargebee. Not dismissable while the move runs: it is one-way, and the
    customer has to see how it ended.
  -->
  <common-modal-shell
    :open="open"
    max-width="max-w-xl"
    panel-class="p-4 sm:p-6"
    :dismissable="state !== 'migrating'"
    :dismiss-on-backdrop="false"
    @close="emit('close')"
  >
    <div class="flex items-start gap-3 pr-8">
      <fa-icon
        :icon="faWallet"
        class="mt-1 size-6 shrink-0 text-brand"
        aria-hidden="true"
      />
      <h2 class="text-lg font-bold text-mouse uppercase">
        {{
          t('checkout/step/create/migration/title', { amount: formattedAmount })
        }}
      </h2>
    </div>

    <template v-if="state === 'offer' || state === 'migrating'">
      <p class="mt-4 text-rhino">
        {{ t('checkout/step/create/migration/intro') }}
      </p>
      <p class="mt-2 text-rhino">
        {{ t('checkout/step/create/migration/rollout') }}
      </p>

      <h3 class="mt-4 font-bold text-mouse">
        {{ t('checkout/step/create/migration/what_happens') }}
      </h3>
      <ul class="mt-2 list-disc space-y-1 pl-5 text-rhino">
        <li>
          {{
            t('checkout/step/create/migration/point/balance', {
              amount: formattedAmount,
            })
          }}
        </li>
        <li>{{ t('checkout/step/create/migration/point/servers') }}</li>
        <li>{{ t('checkout/step/create/migration/point/topup') }}</li>
        <li>{{ t('checkout/step/create/migration/point/invoices') }}</li>
      </ul>

      <div
        class="mt-4 flex items-start gap-2 rounded-lg bg-warning/10 p-3 text-sm text-warning outline outline-warning"
        role="note"
      >
        <fa-icon
          :icon="faTriangleExclamation"
          class="mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <span>{{ t('checkout/step/create/migration/irreversible') }}</span>
      </div>

      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <common-button
          variant="secondary"
          outline
          :disabled="state === 'migrating'"
          @click="emit('close')"
        >
          {{ t('checkout/step/create/migration/button/later') }}
        </common-button>
        <common-button :disabled="state === 'migrating'" @click="migrate">
          {{
            state === 'migrating'
              ? t('checkout/step/create/migration/button/migrating')
              : t('checkout/step/create/migration/button/migrate')
          }}
        </common-button>
      </div>
    </template>

    <!-- The move did not finish in this request. The server queues the resume
         job to finish it; trying again finishes it too, and is safe, so the
         customer is not left waiting on a queue that may be down. unknown: the
         call failed AND the follow-up status read did, so nobody can say. -->
    <template v-else-if="state === 'finishing' || state === 'unknown'">
      <p class="mt-4 text-rhino">
        {{
          state === 'finishing'
            ? t('checkout/step/create/migration/finishing')
            : t('checkout/step/create/migration/unknown')
        }}
      </p>
      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <common-button variant="secondary" outline @click="migrate">
          {{ t('checkout/step/create/button/retry') }}
        </common-button>
        <common-button @click="emit('close')">
          {{ t('checkout/step/create/migration/button/continue') }}
        </common-button>
      </div>
    </template>

    <!-- The server refused: nothing moved, and the customer pays as they would
         have without the offer. The only outcome the modal calls a failure —
         the server said so; a failed request never did. -->
    <template v-else>
      <p class="mt-4 text-danger-fg">
        {{ t('checkout/step/create/migration/refused') }}
      </p>
      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <common-button @click="emit('close')">
          {{ t('checkout/step/create/migration/button/continue') }}
        </common-button>
      </div>
    </template>
  </common-modal-shell>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/nuxt';
import {
  faTriangleExclamation,
  faWallet,
} from '@fortawesome/pro-regular-svg-icons';
import { MigrateOwnAccountBalanceRequest } from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import { MigrationStatus } from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import type { Money } from 'gportal-grpc/gportal/type/v1/money_pb';

const props = defineProps<{
  open: boolean;
  amount: Money;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  // The balance is on Chargebee now; the checkout has to be fetched again for
  // the balance arm to appear.
  (e: 'migrated'): void;
}>();

const { t } = useI18n();
const { format: formatMoney } = useNumberMoneyFormatting();
const buildAuthMetadata = useAuthMetadata();

const paymentStore = usePaymentStore();

const formattedAmount = computed(() => formatMoney(props.amount));

// Whether a fresh status read confirms the migration. False covers both a
// "not migrated" answer and a read that failed: neither proves anything after
// a request that did not come back.
async function confirmedMigrated(): Promise<boolean> {
  paymentStore.invalidateBalanceMigrated();
  await paymentStore.fetchBalanceMigrated();

  return paymentStore.isBalanceMigrated;
}

// offer: the question. migrating: the call is running. finishing: the move did
// not finish and is being completed — a retry is safe. refused: the server
// declined and nothing moved. unknown: the call failed, so the outcome is not
// known — the server may still be moving the balance.
type State = 'offer' | 'migrating' | 'finishing' | 'refused' | 'unknown';
const state = ref<State>('offer');

// A fresh question every time the modal opens.
watch(
  () => props.open,
  (open) => {
    if (open) state.value = 'offer';
  },
);

// The move takes up to the Webinterface's 15 s plus Chargebee, past the
// client's 10 s default. The server finishes it whether or not we wait.
const MIGRATE_TIMEOUT_MS = 60_000;

async function migrate(): Promise<void> {
  state.value = 'migrating';

  const $grpcPayment = useGrpcPayment();
  const { response, error } = await $grpcPayment.executeApiCallActive(
    'migrateOwnAccountBalance',
    new MigrateOwnAccountBalanceRequest({}),
    buildAuthMetadata(),
    MIGRATE_TIMEOUT_MS,
  );

  if (error != null) {
    captureException(error, {
      extra: { context: 'BalanceMigrationModal.migrateOwnAccountBalance' },
    });
    // A timeout or a dropped connection says nothing about the migration: the
    // server carries on without us. Only "migrated" is an answer here — a "not
    // migrated" read may be a snapshot of a move still running, so it is not
    // proof of a failure. Everything else stays unconfirmed, with a retry that
    // is safe whichever way it went.
    if (await confirmedMigrated()) {
      emit('migrated');

      return;
    }
    state.value = 'unknown';

    return;
  }

  switch (response?.status) {
    case MigrationStatus.MIGRATED:
    case MigrationStatus.ALREADY_MIGRATED:
    case MigrationStatus.RESUMED:
      emit('migrated');

      return;
    case MigrationStatus.FAILED_NEEDS_RETRY:
      state.value = 'finishing';

      return;
    default:
      state.value = 'refused';
  }
}
</script>
